import React, { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@ofself/shell';
import { Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import Segmented from '../../components/nucleus/Segmented';
import Picker from '../../components/nucleus/Picker';
import Switch from '../../components/nucleus/Switch';
import StatusPill from '../../components/nucleus/StatusPill';
import { ErrorEmpty } from '../../components/nucleus/Empty';
import { useConfirm } from '../../components/nucleus/actions';
import { ago, until, plural } from '../../nucleus/format';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { Field, blank, missingOf, problemsOf, takesHere, typed } from './fields';
import { pluginsApi, autosApi } from './data/api';
import { useSetupConfig } from './data/hooks';
import { refusalOf, cannotWriteWord } from './data/items';
import {
    fieldLabels, fieldKeys, outcomeWord, outcomeTone, leftOutLine, scheduleWhy, scheduleFix, scheduleWord, listWords, needWord,
} from './data/words';
import {
    realmOption, writeWord, startRealm, toolReads, toolWrites, receiptOf, frequencyOptions, frequencyValue, cronProblem, sameTool,
} from './data/toolruns';
import RequestSheet from './RequestSheet';
import './tool-sheet.css';

// ONE TOOL OF A PLUGIN (plugin lifecycle §3.5) — opened per tool, never with a
// picker inside: what it does and what it asks for at the side, with the realm
// it runs in, then Run | Runs automatically.
//   Run: this run's inputs by label, the realm it runs in (each saying what it
//        lets this tool write; the default marked), the one press, and what came of it.
//   Runs automatically: how it stands and why, the act that lets it run, how
//        often, the realm it runs in, the switch, next and last run. It saves as it changes.
// Read from GET /setup-config?tool_id=, which answers for the space you are
// in: a group's realms in a group, never your own.
//
//   <ToolSheet item tools toolId groupId access gate tab="run|schedule" onTab   ('schedule': Runs automatically)
//       onSetup(step) onClose onChanged onInstall />
// `onSetup(step)` opens the plugin's setup at a step ('account' · 'settings' ·
// 'approve', its Turn on); without it the sheet goes to ?open=<plugin>&setup=<step>.

// what a run's receipt names, at most this many; the rest are counted
const SHOWN = 5;
export const TOOL_TABS = [{ id: 'run', label: 'Run' }, { id: 'schedule', label: 'Runs automatically' }];

// schema names for what a tool reads, read once
let schemaNames = null;
function useSchemaNames(enabled) {
    const [names, setNames] = useState(() => schemaNames || {});
    useEffect(() => {
        if (!enabled || schemaNames) return undefined;
        let live = true;
        Promise.resolve()
            .then(() => api.getSchemas?.())
            .then((list) => {
                schemaNames = Object.fromEntries((list || []).filter((s) => s?.id).map((s) => [String(s.id), s.schema_name || s.name]));
                if (live) setNames(schemaNames);
            })
            .catch(() => {});
        return () => { live = false; };
    }, [enabled]);
    return names;
}

/** What one tool asks for, said once at the sheet's side: what it reads (schema
 *  names), writes, does outside ofSelf, and where its data goes. */
export function AsksFor({ reads = [], writes = [], effects = [], destinations = [], names = {} }) {
    const readWords = reads.map((r) => (r.any ? 'Any schema' : r.name || names[r.schemaId] || 'One schema'));
    if (!readWords.length && !writes.length && !effects.length && !destinations.length) {
        return <Note>Nothing: it reads, writes and sends nothing.</Note>;
    }
    return (
        <div className="plugins-ts__asks">
            {readWords.length > 0 && <Row name="Reads" sub={listWords(readWords)} />}
            {writes.map((w) => <Row key={`w-${w}`} name="Writes" sub={writeWord(w)} />)}
            {effects.map((e, i) => (
                <Row key={`e${i}`} name="Outside ofSelf" sub={e.description} end={e.reversible ? undefined : 'Can’t be undone'} endTone={e.reversible ? undefined : 'warn'} />
            ))}
            {destinations.length > 0 && <Row name="Data goes to" sub={destinations.join(', ')} />}
        </div>
    );
}

/** What a run came to: its outcome, what it did (the non-zero counts), what it
 *  proposed (a door to Review), what it left out and why, and the door to the run. */
export function Receipt({ receipt, onOpenNode, onReview, onOpenRun, whose = 'The realm you chose' }) {
    const r = receipt;
    const cannot = cannotWriteWord(r.cannotWrite, whose);
    const named = [...r.made.map((x) => ({ ...x, did: 'made' })), ...r.changed.map((x) => ({ ...x, did: 'changed' }))];
    const rest = Math.max(0, named.length - SHOWN);
    return (
        <div className="plugins-ts__receipt">
            <div className="plugins-ts__outcome">
                <StatusPill status={r.outcome} of="run" tone={outcomeTone(r.outcome)}>{outcomeWord(r.outcome)}</StatusPill>
                {r.counts.map((c) => <span key={c.label} className="plugins-ts__count">{`${c.label} ${c.n}`}</span>)}
                {r.proposed > 0 && (
                    <button type="button" className="nx-link" onClick={onReview}>{`Proposed ${r.proposed} · Review`}</button>
                )}
            </div>
            {r.replay && <Note>Already ran — this is that run.</Note>}
            {r.unknown && <Note tone="warn">It may have run; check before running again.</Note>}
            {r.message && <Note>{r.message}</Note>}
            {named.slice(0, SHOWN).map((x) => (
                <Row key={`${x.did}-${x.id}`} name={x.title || 'Untitled'} end={x.did} onPress={() => onOpenNode(x.id)} />
            ))}
            {rest > 0 && <Note>{`${plural(rest, 'more node')}.`}</Note>}
            {cannot && <Note tone="warn">{`${cannot}.`}</Note>}
            {r.leftOut.map((d) => <Row key={d.why} name={leftOutLine(d)} end={String(d.count)} endTone="warn" />)}
            {r.runId && <button type="button" className="nx-link plugins-ts__door" onClick={() => onOpenRun(r.runId)}>Open the run</button>}
        </div>
    );
}

export default function ToolSheet({
    item, tools, toolId, groupId, access, gate, tab: givenTab = 'run', onTab, onSetup, onClose, onChanged, onInstall,
}) {
    const navigate = useNavigate();
    const { to } = usePlace();
    const tool = tools.find((t) => sameTool(t.id, toolId)) || tools[0];
    const tid = tool?.id || '';
    const setup = useSetupConfig(groupId, item.id, tid || null);
    const cfg = setup.data;
    const [tab, setTabState] = useState(givenTab === 'schedule' ? 'schedule' : 'run');
    useEffect(() => { setTabState(givenTab === 'schedule' ? 'schedule' : 'run'); }, [givenTab]);
    const setTab = (t) => { setTabState(t); onTab?.(t); };
    const [request, setRequest] = useState(null);
    const uid = useId().replace(/:/g, '');
    // THE REALM A RUN RUNS IN, chosen on the Run tab and said in the head, so it is never a surprise
    const runRealms = cfg?.run_realms || [];
    const [realmId, setRealmId] = useState(() => startRealm(runRealms, null));
    useEffect(() => { setRealmId((cur) => startRealm(runRealms, cur)); }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
    // on Runs automatically, the realm it runs in there
    const timed = (cfg?.schedules || []).find((x) => sameTool(x.tool_id, tid)) || null;
    const timedRealmId = timed?.privacy_realm_id || (cfg?.lifecycle?.tools || []).find((t) => sameTool(t.tool_id, tid))?.schedule?.privacy_realm_id || null;
    const runsIn = tab === 'schedule'
        ? (timedRealmId ? [...(cfg?.eligible_realms || []), ...runRealms].find((r) => String(r.id) === String(timedRealmId)) || (timed?.privacy_realm_name ? { name: timed.privacy_realm_name } : null) : null)
        : runRealms.find((r) => String(r.id) === String(realmId)) || null;

    const state = (cfg?.tools || []).find((s) => sameTool(s.tool_id, tid)) || null;
    const description = tool?.description || state?.description || '';
    const reads = toolReads(cfg?.reads, tid);
    const names = useSchemaNames(reads.some((r) => !r.any && !r.name));
    const writes = toolWrites(cfg?.run_realms?.length ? cfg.run_realms : cfg?.eligible_realms, tid);
    const declaredWrites = (tool?.writes || []).map((w) => `${w.verb} ${w.what}`);

    const close = () => onClose?.();
    const changed = () => { setup.reload(); onChanged?.(); };
    // a fix that is the plugin's setup: the inspector's setup sheet, at that step
    const goSetup = (step) => {
        if (onSetup) { onSetup(step); return; }
        close();
        navigate(to('plugins', null, `open=${encodeURIComponent(item.id)}&setup=${step}`));
    };

    return (
        <Modal open onClose={close} label={`${tool?.name || item.name} · ${item.name}`} className="plugins-ts-modal">
            <div className="plugins-ts">
                <aside className="plugins-ts__side" aria-label="What it does">
                    <span className="plugins-ts__kicker">{`Tool · ${item.name}`}</span>
                    <h2 className="plugins-ts__title">{tool?.name || item.name}</h2>
                    {runsIn && <p className="plugins-ts__runsin">{`Runs in ${runsIn.name}`}</p>}
                    {description && <p className="plugins-ts__about">{description}</p>}
                    <span className="plugins-ts__head">It asks for</span>
                    {cfg && (
                        <AsksFor
                            reads={reads} names={names}
                            writes={writes.length ? writes : declaredWrites}
                            effects={state?.effects || tool?.effects || []}
                            destinations={cfg?.data_destinations || []}
                        />
                    )}
                </aside>
                <div className="plugins-ts__main">
                    <Segmented label="Run, or runs automatically" options={TOOL_TABS} value={tab} onChange={setTab} />
                    {setup.error && !cfg && <ErrorEmpty what="this tool" error={setup.error} retry={setup.reload} />}
                    {cfg && tab === 'run' && (
                        <RunTab
                            key={`run-${tid}`} uid={uid} item={item} tool={tool} tid={tid} cfg={cfg} groupId={groupId} access={access} gate={gate}
                            realmId={realmId} onRealm={setRealmId} onChanged={changed} onInstall={onInstall} onRequest={setRequest}
                            onOpenNode={(id) => navigate(to('graph', null, `open=${encodeURIComponent(id)}`))}
                            onReview={() => navigate(to('graph', 'review'))}
                            onOpenRun={(id) => { close(); navigate(to('plugins', 'runs', `open=${encodeURIComponent(id)}`)); }}
                        />
                    )}
                    {cfg && tab === 'schedule' && (
                        <ScheduleTab
                            key={`schedule-${tid}`} uid={uid} item={item} tool={tool} tid={tid} cfg={cfg} groupId={groupId} access={access} gate={gate}
                            onChanged={changed} onSetup={goSetup} onClose={close}
                            onOpenRealm={(id) => { close(); navigate(to('realms', null, `open=${encodeURIComponent(id)}`)); }}
                        />
                    )}
                </div>
            </div>
            {request && (
                <RequestSheet groupId={groupId} requestId={request} onClose={() => setRequest(null)} onAnswered={() => changed()} />
            )}
        </Modal>
    );
}

// ── Run ──────────────────────────────────────────────────────────────────────

function RunTab({
    uid, item, tool, tid, cfg, groupId, access, gate, realmId, onRealm: setRealmId, onChanged, onInstall, onRequest, onOpenNode, onReview, onOpenRun,
}) {
    const [values, setValues] = useState({});
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    // the fields the server said this run needs (a 400 naming them)
    const [wanted, setWanted] = useState([]);
    const realms = cfg.run_realms || [];

    const lifecycle = cfg.lifecycle || item.lifecycle || null;
    const life = (lifecycle?.tools || []).find((t) => sameTool(t.tool_id, tid)) || null;
    const runtime = (cfg.fields || []).filter((f) => !f.secret && f.scope === 'runtime');
    const valueOf = (f) => values[f.key] ?? f.value ?? f.default ?? '';
    const current = Object.fromEntries(runtime.map((f) => [f.key, valueOf(f)]));
    const missing = missingOf(runtime, current);
    const broken = problemsOf(runtime, current);

    // WHAT HOLDS THE PRESS, said beside it (never only in a tooltip)
    const needs = [...(lifecycle?.needs || []), ...(life?.needs || tool?.needs || [])];
    const setupLabels = [...new Set(needs.filter((n) => n.kind === 'credentials' || n.kind === 'parameters').flatMap((n) => fieldLabels(n.fields)))];
    const installed = lifecycle ? lifecycle.installed !== false : true;
    let blocker = null;
    if (!installed) blocker = 'It isn’t set up here';
    else if (!access.runPlugins) blocker = access.pluginHint;
    else if (needs.some((n) => n.kind === 'connection')) blocker = `${needWord(needs.find((n) => n.kind === 'connection'))} first`;
    else if (needs.some((n) => n.kind === 'credentials' || n.kind === 'parameters')) blocker = `Set ${listWords(setupLabels) || 'its setup'} first`;
    else if (runtime.some((f) => !takesHere(f) && f.required)) blocker = 'It takes a file, which an app gives it';
    else if (!realms.length) blocker = 'No realm here lets it run';
    else if (!realmId) blocker = 'Choose a realm it runs in';
    else if (missing.length) blocker = `${listWords(missing)} ${missing.length === 1 ? 'is' : 'are'} needed`;
    else if (broken.length) blocker = `${broken[0].label}: ${broken[0].problem.toLowerCase()}`;

    const labelOf = (key) => runtime.find((f) => f.key === key)?.label || key;
    const run = async () => {
        setBusy(true); setResult(null); setWanted([]);
        try {
            const input = {};
            runtime.forEach((f) => { const v = values[f.key]; if (!blank(v)) input[f.key] = typed(f, v); });
            const res = await pluginsApi.run(item.id, groupId, { toolId: tid || null, realmId, input });
            // a parked run is not a failed one: it waits for a yes
            if (res?.consent_required) setResult({ parked: { id: res.request_id || null } });
            else setResult({ receipt: receiptOf(res?.execution || {}) });
            onChanged?.();
        } catch (e) {
            const named = e?.code === 'VALIDATION_ERROR' ? fieldKeys(e?.info?.missing_fields).filter((k) => runtime.some((f) => f.key === k)) : [];
            setWanted(named);
            // a run that may have happened (no answer in time), or one already run, still says so
            const ex = e?.info?.execution || null;
            setResult({
                error: refusalOf(e, labelOf),
                notInstalled: e?.code === 'NOT_INSTALLED',
                receipt: ex && (ex.outcome_unknown || ex.idempotent_replay) ? receiptOf(ex) : null,
            });
        } finally {
            setBusy(false);
        }
    };

    const realmPicker = `${uid}-run-realm`;
    const heldId = `${uid}-run-held`;
    const memberRealm = groupId && !gate.isAdmin && access.realmName;
    return (
        <section className="plugins-ts__tab" aria-label="Run">
            {runtime.length === 0 && <p className="plugins-ts__quiet">It takes no inputs.</p>}
            {runtime.map((f) => (
                <Field
                    key={f.key} field={f} groupId={groupId} value={valueOf(f)} missing={wanted.includes(f.key)}
                    onChange={(v) => { setValues((cur) => ({ ...cur, [f.key]: v })); setWanted((w) => w.filter((k) => k !== f.key)); }}
                />
            ))}
            <div className="plugins-field">
                <span className="plugins-label">Runs in</span>
                <Picker
                    id={realmPicker} fill label="Runs in" value={realmId} onChange={setRealmId}
                    placeholder={realms.length ? 'Choose a realm' : 'No realm here lets it run'}
                    options={realms.map((r) => realmOption(r, tid))}
                />
                {memberRealm && <span className="plugins-field__note">{`Your runs are in ${access.realmName}`}</span>}
            </div>
            <SheetFoot className="plugins-ts__foot">
                <FootButton primary busy={busy} disabled={!!blocker || busy} onClick={run} aria-describedby={blocker ? heldId : undefined}>Run</FootButton>
            </SheetFoot>
            {blocker && <p id={heldId} className="plugins-ts__held">{`${blocker}.`}</p>}
            <div className="plugins-ts__said" aria-live="polite">
                {result?.parked && (
                    <div className="plugins-ts__parked">
                        <StatusPill status="waiting" of="run">Waiting for you</StatusPill>
                        {result.parked.id
                            ? <button type="button" className="nx-link" onClick={() => onRequest(result.parked.id)}>Allow</button>
                            : <span className="plugins-ts__quiet">It waits in Runs.</span>}
                    </div>
                )}
                {result?.error && (
                    <p className="plugins-error" role="alert">
                        {result.error}
                        {result.notInstalled && gate.isAdmin && onInstall && <>{' '}<button type="button" className="nx-link" onClick={onInstall}>Set up</button></>}
                    </p>
                )}
                {result?.receipt && <Receipt receipt={result.receipt} onOpenNode={onOpenNode} onReview={onReview} onOpenRun={onOpenRun} />}
            </div>
        </section>
    );
}

// ── Runs automatically ───────────────────────────────────────────────────────

function ScheduleTab({ uid, item, tool, tid, cfg, groupId, access, gate, onChanged, onSetup, onClose, onOpenRealm }) {
    const confirm = useConfirm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const lifecycle = cfg.lifecycle || item.lifecycle || null;
    const life = (lifecycle?.tools || []).find((t) => sameTool(t.tool_id, tid)) || null;
    const schedule = life?.schedule || tool?.schedule || { state: 'off' };
    const row = (cfg.schedules || []).find((s) => sameTool(s.tool_id, tid)) || null;
    const eligible = cfg.eligible_realms || [];
    const enabled = !!row?.enabled;
    const realmOf = row?.privacy_realm_id || schedule.privacy_realm_id || '';
    const freq = frequencyValue(row?.frequency || schedule.frequency, row?.cron);
    const custom = !!freq && !frequencyOptions().some((o) => o.value === freq);
    const [advanced, setAdvanced] = useState(custom);
    const [cron, setCron] = useState(row?.cron || '');
    useEffect(() => { setCron(row?.cron || ''); }, [row?.cron]);

    // who may change what: an admin edits; a member may switch it off when their realm runs plugins
    const canEdit = gate.isAdmin && cfg.can_edit_schedule !== false;
    const canRealm = canEdit && cfg.can_choose_realm !== false;
    const switchHeld = enabled
        ? (gate.isAdmin || access.runPlugins ? null : access.pluginHint)
        : !gate.isAdmin ? gate.hint : !realmOf ? 'Choose a realm it runs in first' : null;

    const put = async (change) => {
        setBusy(true); setError(null);
        try {
            await pluginsApi.save(item.id, groupId, { ...(tid && !('require_consent_per_run' in change) ? { tool_id: tid } : {}), ...change });
            onChanged?.();
        } catch (e) {
            setError(e?.code === 'REALM_REQUIRED' ? 'Choose a realm it runs in first.' : e?.message || 'Could not save.');
        } finally {
            setBusy(false);
        }
    };

    // THE ACT THAT LETS IT RUN, by why it can't
    const realmPicker = `${uid}-schedule-realm`;
    const stuck = ['paused', 'needs_realm'].includes(schedule.state);
    const fix = !stuck ? null : schedule.reason === 'plugin_paused' ? { id: 'back', label: `Back to ${item.name}` } : scheduleFix(schedule);
    const adminFix = fix && !['back', 'open_realm'].includes(fix.id);
    const fixHeld = adminFix && !gate.isAdmin ? gate.hint : fix?.id === 'realm' && !canRealm ? 'An admin chooses this' : null;
    const runFix = async () => {
        switch (fix.id) {
            case 'approve': onSetup('approve'); break;
            case 'connect': onSetup('account'); break;
            case 'settings': onSetup('settings'); break;
            case 'stop_asking': await put({ require_consent_per_run: false }); break;
            case 'realm': document.getElementById(realmPicker)?.focus(); break;
            case 'open_realm': if (realmOf) onOpenRealm(realmOf); break;
            case 'back': onClose(); break;
            case 'delete_schedule': {
                const ok = await confirm({
                    title: 'Delete it?', body: 'Its tool is gone, so it can’t run again. Its runs stay.', label: 'Delete', danger: true,
                });
                if (!ok || !row?.automation_id) return;
                setBusy(true);
                try { await autosApi.remove(groupId, row.automation_id); onChanged?.(); } catch (e) { setError(e?.message || 'Could not delete it.'); } finally { setBusy(false); }
                break;
            }
            default: break;
        }
    };

    const why = scheduleWhy(schedule);
    // it runs, and leaves out what its realm does not let it write: a warning, not a block
    const leaves = schedule.state !== 'paused' && schedule.reason !== 'realm_cannot_write' ? cannotWriteWord(schedule.cannot_write) : null;
    const realmOptions = [
        ...eligible.map((r) => realmOption(r, tid)),
        ...(realmOf && !eligible.some((r) => String(r.id) === String(realmOf)) ? [{ value: realmOf, label: row?.privacy_realm_name || 'Its realm' }] : []),
    ];
    const madeBy = schedule.made_by_app?.name || null;
    const fixHeldId = `${uid}-fix-held`;
    const switchHeldId = `${uid}-switch-held`;
    const typedCron = cron.trim();
    const cronIssue = typedCron && typedCron !== (row?.cron || '') ? cronProblem(typedCron) : null;
    const saveCron = () => {
        if (!typedCron || typedCron === (row?.cron || '') || cronProblem(typedCron)) return;
        put({ cron: typedCron });
    };

    return (
        <section className="plugins-ts__tab" aria-label="Runs automatically">
            <div className="plugins-ts__status">
                <StatusPill status={schedule.state} of="schedule">{scheduleWord(schedule.state)}</StatusPill>
                {why && <span className="plugins-ts__why"><b>Why</b>{` ${why.replace(/\.?$/, '.')}`}</span>}
                {fix && (
                    <FootButton danger={!!fix.danger} disabled={!!fixHeld || busy} onClick={runFix} aria-describedby={fixHeld ? fixHeldId : undefined} className="plugins-ts__fix">
                        {fix.label}
                    </FootButton>
                )}
            </div>
            {fixHeld && <p id={fixHeldId} className="plugins-ts__held">{`${fixHeld}.`}</p>}
            {leaves && <Note tone="warn">{`${leaves}, so each run leaves that out.`}</Note>}
            {schedule.state === 'failing' && schedule.last_error && <Note tone="live">{schedule.last_error}</Note>}
            {madeBy && <Facts items={[{ label: 'Made by', value: madeBy }]} />}

            <div className="plugins-field">
                <span className="plugins-label">How often</span>
                <Picker
                    fill label="How often" value={freq} placeholder="Choose how often" disabled={!canEdit || busy}
                    options={frequencyOptions(freq)} onChange={(v) => put({ cron: v })}
                />
                {!canEdit && <span className="plugins-field__note">An admin chooses this</span>}
                {canEdit && (
                    <button type="button" className="nx-link plugins-ts__fold" aria-expanded={advanced} onClick={() => setAdvanced((v) => !v)}>
                        {advanced ? 'Hide advanced' : 'Advanced'}
                    </button>
                )}
                {canEdit && advanced && (
                    <label className="plugins-ts__cron">
                        <span className="plugins-label">Minute hour day month weekday</span>
                        <input
                            className="plugins-input is-mono" value={cron} placeholder="0 7 * * *" disabled={busy}
                            aria-invalid={cronIssue ? true : undefined}
                            onChange={(e) => setCron(e.target.value)} onBlur={saveCron}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveCron(); } }}
                        />
                        {cronIssue && <span className="plugins-field__error">{cronIssue}</span>}
                    </label>
                )}
            </div>

            <div className="plugins-field">
                <span className="plugins-label">Runs in</span>
                <Picker
                    id={realmPicker} fill label="Runs in" value={realmOf}
                    placeholder={eligible.length ? 'Choose a realm' : 'No realm here lets it run'}
                    disabled={!canRealm || busy} options={realmOptions}
                    onChange={(v) => put({ privacy_realm_id: v })}
                />
                {!canRealm && <span className="plugins-field__note">An admin chooses this</span>}
            </div>

            <div className="plugins-field plugins-ts__switch">
                <span className="plugins-label">On</span>
                <Switch
                    aria-label="Runs automatically" checked={enabled} disabled={busy || !!switchHeld}
                    aria-describedby={switchHeld ? switchHeldId : undefined}
                    onChange={(e) => put({ enabled: e.target.checked })}
                />
                {switchHeld && <span id={switchHeldId} className="plugins-field__note">{`${switchHeld}.`}</span>}
            </div>

            <Facts items={[
                { label: 'Next run', value: schedule.next_run_at && enabled ? until(schedule.next_run_at) : '—' },
                {
                    label: 'Last run',
                    value: schedule.last_run_at ? `${ago(schedule.last_run_at)}${schedule.last_status ? ` · ${outcomeWord(schedule.last_status)}` : ''}` : 'Never',
                    tone: schedule.last_status === 'failed' ? 'live' : undefined,
                },
            ]} />
            <div aria-live="polite">{error && <p className="plugins-error" role="alert">{error}</p>}</div>
        </section>
    );
}
